/* import type { VariantProps } from 'class-variance-authority'

import * as React from 'react'
import { cva } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'

const alertVariants = cva(
	'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
	{
		variants: {
			variant: {
				default: 'bg-card text-card-foreground',
				neutral: 'bg-secondary',
				destructive:
					'border-red-200 bg-red-50 text-red-700 *:data-[slot=alert-description]:text-red-600 dark:bg-red-950/50 dark:text-red-200 dark:border-red-800/50 dark:text-red-100 dark:*:data-[slot=alert-description]:text-red-200/80',
				success:
					'border-green-200 bg-green-50 text-green-700 *:data-[slot=alert-description]:text-green-600 dark:bg-green-950/50 dark:text-green-200 dark:border-green-800/50 dark:text-green-100 dark:*:data-[slot=alert-description]:text-green-200/80',
				info: 'border-sky-200 text-sky-700 bg-sky-50 *:data-[slot=alert-description]:text-sky-700/80 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800/50 dark:text-sky-100 dark:*:data-[slot=alert-description]:text-sky-200/80',
				warning:
					'border-orange-200 text-orange-700 bg-orange-50 *:data-[slot=alert-description]:text-orange-700/80 dark:bg-orange-950/50 dark:text-orange-200 dark:border-orange-800/50 dark:text-orange-100 dark:*:data-[slot=alert-description]:text-orange-200/80',
			},
		},
		defaultVariants: { variant: 'default' },
	}
)

function Alert({
	className,
	variant,
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
	return (
		<div
			data-slot='alert'
			role='alert'
			className={cn(alertVariants({ variant }), className)}
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='alert-title'
			className={cn(
				'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
				className
			)}
			{...props}
		/>
	)
}

function AlertDescription({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='alert-description'
			className={cn(
				'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
				className
			)}
			{...props}
		/>
	)
}

export { Alert, AlertTitle, AlertDescription, alertVariants }
 */
import type { VariantProps } from 'class-variance-authority'

import * as React from 'react'
import { cva } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'

const alertVariants = cva(
	"group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
	{
		variants: {
			variant: {
				default: 'bg-card text-card-foreground',
				neutral: 'bg-secondary',
				destructive:
					'border-red-200 bg-red-50 text-red-700 *:data-[slot=alert-description]:text-red-600 dark:bg-red-950/50 dark:text-red-200 dark:border-red-800/50 dark:text-red-100 dark:*:data-[slot=alert-description]:text-red-200/80',
				success:
					'border-green-200 bg-green-50 text-green-700 *:data-[slot=alert-description]:text-green-600 dark:bg-green-950/50 dark:text-green-200 dark:border-green-800/50 dark:text-green-100 dark:*:data-[slot=alert-description]:text-green-200/80',
				info: 'border-sky-200 text-sky-700 bg-sky-50 *:data-[slot=alert-description]:text-sky-700/80 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800/50 dark:text-sky-100 dark:*:data-[slot=alert-description]:text-sky-200/80',
				warning:
					'border-orange-200 text-orange-700 bg-orange-50 *:data-[slot=alert-description]:text-orange-700/80 dark:bg-orange-950/50 dark:text-orange-200 dark:border-orange-800/50 dark:text-orange-100 dark:*:data-[slot=alert-description]:text-orange-200/80',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	}
)

function Alert({
	className,
	variant,
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
	return (
		<div
			data-slot='alert'
			role='alert'
			className={cn(alertVariants({ variant }), className)}
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='alert-title'
			className={cn(
				'cn-font-heading font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
				className
			)}
			{...props}
		/>
	)
}

function AlertDescription({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='alert-description'
			className={cn(
				'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
				className
			)}
			{...props}
		/>
	)
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='alert-action'
			className={cn('absolute top-2 right-2', className)}
			{...props}
		/>
	)
}

export { Alert, AlertTitle, AlertDescription, AlertAction, alertVariants }
